import { Component, Inject, ViewChild, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { BasePreviewComponent } from '../base-preview.component';
import { AudioMiniPlayerService } from '../audio-mini-player/audio-mini-player.service';
import { UNSUPPORTED_FILE_PREVIEW_MESSAGE } from 'src/app/constants/app.constants';
import { UserService } from 'src/app/services/core/user.service';
import { DocumentMarkupService } from 'src/app/modules/document/document-image-annotator/document-markup.data.service';
import { Comment as MediaComment, CommentPriority, CommentStatus } from 'src/app/modules/document/document-image-annotator/media-annotation.types';

let videoCmtSeq = 0;

@Component({
    selector: 'app-video-preview',
    templateUrl: './video-preview.component.html',
    styleUrls: ['./video-preview.component.scss'],
    standalone: false
})
export class VideoPreviewComponent extends BasePreviewComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer', { static: false }) videoPlayer!: ElementRef<HTMLVideoElement>;

  // Video State
  isPlaying = false;
  isLoading = true;
  currentTime = 0;
  duration = 0;
  buffered = 0;
  volume = 1;
  isMuted = false;
  playbackRate = 1;
  isFullscreen = false;
  showControls = true;

  // UI State
  isDraggingTimeline = false;
  controlsTimeout: any;
  playbackRateOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Comments (timestamp-anchored) + PiP mini-player
  documentId = '';
  isPreDefined = false;
  comments: MediaComment[] = [];
  showCommentsSidebar = false;
  commentArmed = false;
  activeCommentId: string | null = null;
  newCommentText = '';
  newCommentTime: number | null = null;
  private commentsLoaded = false;
  private commentsDirty = false;
  private commentSaveTimer: any = null;

  constructor(
    public override dialogRef: MatDialogRef<VideoPreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public override data: any,
    private mini: AudioMiniPlayerService,
    private userService: UserService,
    private markupService: DocumentMarkupService,
    private toastr: ToastrService
  ) {
    super(dialogRef, data);
    this.documentId = this.model?.id || '';
    this.isPreDefined = this.model?.isPreDefined || false;
  }

  ngOnInit(): void {
    this.loadComments();
  }

  ngOnDestroy(): void {
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }
    this.flushComments();
  }

  get currentUserName(): string {
    const n = (this.userService.getFullName() || '').replace(/\b(null|undefined)\b/g, '').replace(/\s+/g, ' ').trim();
    return n || 'You';
  }

  // ========================================
  // Video Event Handlers
  // ========================================

  onVideoLoaded(): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      this.duration = video.duration;
      this.isLoading = false;
      // Resume position/state when re-opened from the mini-player.
      if (this.data?.startTime) { try { video.currentTime = this.data.startTime; } catch {} this.data.startTime = 0; }
      if (this.data?.autoplay) { this.data.autoplay = false; video.play().catch(() => {}); }
    }
  }

  onTimeUpdate(): void {
    const video = this.videoPlayer?.nativeElement;
    if (video && !this.isDraggingTimeline) {
      this.currentTime = video.currentTime;
      this.updateBuffered();
    }
  }

  onPlay(): void {
    this.isPlaying = true;
  }

  onPause(): void {
    this.isPlaying = false;
  }

  onWaiting(): void {
    this.isLoading = true;
  }

  onCanPlay(): void {
    this.isLoading = false;
  }

  /**
   * Fired when the browser cannot load/decode the video (unsupported
   * container/codec, e.g. .mkv / .wmv / .avi / .mpg / .flv / .ts). Show the
   * generic unsupported-file toast and close the preview.
   */
  onVideoError(): void {
    this.isLoading = false;
    this.toastr.error(UNSUPPORTED_FILE_PREVIEW_MESSAGE);
    this.dialogRef.close();
  }

  updateBuffered(): void {
    const video = this.videoPlayer?.nativeElement;
    if (video && video.buffered.length > 0) {
      this.buffered = video.buffered.end(video.buffered.length - 1);
    }
  }

  // ========================================
  // Playback Controls
  // ========================================

  togglePlay(): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      if (this.isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    }
  }

  seekTo(time: number): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      video.currentTime = time;
      this.currentTime = time;
    }
  }

  skip(seconds: number): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
    }
  }

  onTimelineClick(event: MouseEvent): void {
    const timeline = event.currentTarget as HTMLElement;
    const rect = timeline.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * this.duration;
    this.seekTo(newTime);
  }

  onTimelineDragStart(event: MouseEvent): void {
    this.isDraggingTimeline = true;
    this.onTimelineDrag(event);
  }

  @HostListener('document:mousemove', ['$event'])
  onTimelineDrag(event: MouseEvent): void {
    if (!this.isDraggingTimeline) return;

    const timeline = document.querySelector('.timeline-bar') as HTMLElement;
    if (timeline) {
      const rect = timeline.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = clickX / rect.width;
      const newTime = percentage * this.duration;
      this.currentTime = newTime;
    }
  }

  @HostListener('document:mouseup')
  onTimelineDragEnd(): void {
    if (this.isDraggingTimeline) {
      this.isDraggingTimeline = false;
      this.seekTo(this.currentTime);
    }
  }

  // ========================================
  // Volume Controls
  // ========================================

  toggleMute(): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      video.muted = !video.muted;
      this.isMuted = video.muted;
    }
  }

  setVolume(value: number): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      video.volume = value;
      this.volume = value;
      this.isMuted = value === 0;
    }
  }

  // ========================================
  // Playback Speed
  // ========================================

  setPlaybackRate(rate: number): void {
    const video = this.videoPlayer?.nativeElement;
    if (video) {
      video.playbackRate = rate;
      this.playbackRate = rate;
    }
  }

  // ========================================
  // Fullscreen
  // ========================================

  toggleFullscreen(): void {
    const wrapper = document.querySelector('.video-preview-wrapper') as HTMLElement;
    if (!wrapper) return;

    if (!this.isFullscreen) {
      if (wrapper.requestFullscreen) {
        wrapper.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!document.fullscreenElement;
  }

  // ========================================
  // Keyboard Shortcuts
  // ========================================

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    const video = this.videoPlayer?.nativeElement;
    if (!video) return;

    // Don't hijack keys while typing (e.g. the comment box).
    const el = event.target as HTMLElement;
    const tag = el?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;

    switch (event.key) {
      case ' ':
      case 'k':
        event.preventDefault();
        this.togglePlay();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.skip(-5);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.skip(5);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setVolume(Math.min(1, this.volume + 0.1));
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.setVolume(Math.max(0, this.volume - 0.1));
        break;
      case 'm':
        event.preventDefault();
        this.toggleMute();
        break;
      case 'f':
        event.preventDefault();
        this.toggleFullscreen();
        break;
      case 'Escape':
        if (this.isFullscreen) {
          event.preventDefault();
          this.toggleFullscreen();
        }
        break;
    }
  }

  // ========================================
  // Auto-hide Controls
  // ========================================

  @HostListener('mousemove')
  onMouseMove(): void {
    this.showControls = true;
    this.resetControlsTimeout();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.isPlaying) {
      this.showControls = false;
    }
  }

  resetControlsTimeout(): void {
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }

    if (this.isPlaying) {
      this.controlsTimeout = setTimeout(() => {
        this.showControls = false;
      }, 3000);
    }
  }

  // ========================================
  // Utility Methods
  // ========================================

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  getTimelineProgress(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  getBufferedProgress(): number {
    return this.duration > 0 ? (this.buffered / this.duration) * 100 : 0;
  }

  getVolumeIcon(): string {
    if (this.isMuted || this.volume === 0) return 'fa-volume-mute';
    if (this.volume < 0.5) return 'fa-volume-down';
    return 'fa-volume-up';
  }

  // ========================================
  // Minimize to the floating video mini-player
  // ========================================
  minimizeToMiniPlayer(): void {
    const video = this.videoPlayer?.nativeElement;
    const currentTime = video?.currentTime || 0;
    const isPlaying = !!video && !video.paused;
    if (video) video.pause();
    this.flushComments();
    this.mini.open(this.model, this.getMediaUrl(), this.model?.original_name || 'Video File', currentTime, isPlaying, true);
    this.dialogRef.close();
  }

  // ========================================
  // Comments (timestamp-anchored)
  // ========================================
  toggleCommentsSidebar(): void {
    this.showCommentsSidebar = !this.showCommentsSidebar;
    this.commentArmed = this.showCommentsSidebar;
  }
  /** Clicking the video: while comments are armed → add a comment at the current
   *  time; otherwise the normal play/pause toggle. */
  onVideoClick(): void {
    if (this.commentArmed && this.newCommentTime === null) { this.startAddComment(); return; }
    this.togglePlay();
  }
  startAddComment(): void {
    const video = this.videoPlayer?.nativeElement;
    this.newCommentTime = video ? video.currentTime : 0;
    this.newCommentText = '';
    this.showCommentsSidebar = true;
  }
  addComment(): void {
    if (this.newCommentTime === null || !this.newCommentText.trim()) return;
    const c: MediaComment = {
      id: `cmt-${Date.now()}-${videoCmtSeq++}`,
      x: 0, y: 0,
      mediaTime: this.newCommentTime,
      text: this.newCommentText.trim(),
      author: this.currentUserName,
      timestamp: new Date().toISOString(),
      status: 'open', priority: 'medium', replies: []
    };
    this.comments = [...this.comments, c];
    this.activeCommentId = c.id;
    this.newCommentTime = null;
    this.newCommentText = '';
    this.saveCommentsDebounced();
  }
  cancelComment(): void { this.newCommentTime = null; this.newCommentText = ''; }

  onCommentReply(e: { commentId: string; text: string }): void {
    this.comments = this.comments.map(c => c.id === e.commentId
      ? { ...c, replies: [...c.replies, { id: `r-${Date.now()}`, text: e.text, author: this.currentUserName, timestamp: new Date().toISOString() }] }
      : c);
    this.saveCommentsDebounced();
  }
  onCommentStatus(e: { id: string; status: CommentStatus }): void {
    this.comments = this.comments.map(c => c.id === e.id ? { ...c, status: e.status } : c);
    this.saveCommentsDebounced();
  }
  onCommentPriority(e: { id: string; priority: CommentPriority }): void {
    this.comments = this.comments.map(c => c.id === e.id ? { ...c, priority: e.priority } : c);
    this.saveCommentsDebounced();
  }
  deleteComment(id: string): void {
    this.comments = this.comments.filter(c => c.id !== id);
    if (this.activeCommentId === id) this.activeCommentId = null;
    this.saveCommentsDebounced();
  }
  /** Click a comment → seek the video to its timestamp. */
  selectComment(id: string | null): void {
    this.activeCommentId = id;
    const c = id ? this.comments.find(x => x.id === id) : null;
    if (c && typeof c.mediaTime === 'number') this.seekTo(c.mediaTime);
  }

  private loadComments(): void {
    if (!this.documentId) { this.commentsLoaded = true; return; }
    this.markupService.getMarkup(this.documentId, this.isPreDefined).subscribe({
      next: r => { this.comments = (r?.comments as MediaComment[]) || []; this.commentsLoaded = true; },
      error: () => { this.commentsLoaded = true; }
    });
  }
  private saveCommentsDebounced(): void {
    this.commentsDirty = true;
    if (this.commentSaveTimer) clearTimeout(this.commentSaveTimer);
    this.commentSaveTimer = setTimeout(() => this.flushComments(), 600);
  }
  private flushComments(): void {
    if (this.commentSaveTimer) { clearTimeout(this.commentSaveTimer); this.commentSaveTimer = null; }
    if (!this.commentsDirty || !this.documentId || !this.commentsLoaded) return;
    this.commentsDirty = false;
    this.markupService.saveMarkup(this.documentId, [], this.comments, this.isPreDefined).subscribe({ error: () => {} });
  }
}
